import { useCallback, useEffect, useState } from 'react';
import type { GuestInvite } from '@liveforge/shared';
import { errorMessage } from '../lib/api';
import { listGuestInvites } from '../lib/resources';

export interface GuestInvitesResult {
  invites: GuestInvite[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Lets a page apply a create/delete result without a round trip. */
  setInvites: (invites: GuestInvite[]) => void;
}

export function useGuestInvites(liveId: string | undefined): GuestInvitesResult {
  const [invites, setInvites] = useState<GuestInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!liveId) {
      setLoading(false);
      setError('No live was requested.');
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    listGuestInvites(liveId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setInvites(data);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [liveId, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { invites, loading, error, reload, setInvites };
}
